import { addWorkingDays, endOfTask, nextWorkingDay, startOfTask, } from './calendar';
import { maxDate, requireIsoDate } from './date';
import { buildGraph } from './graph';
import { err, ok, } from './types';
/**
 * Earliest start a single relationship permits for its successor.
 *
 * For FF and SF the relationship constrains the successor's FINISH, so the
 * corresponding start is derived by stepping back over the duration. The
 * derivation is monotonic, which is why taking the maximum over all
 * relationships afterwards is sound.
 */
export function earliestStartFrom(calendar, dependency, predecessor, successorDuration) {
    const lag = dependency.lagDays;
    switch (dependency.type) {
        case 'FS':
            return addWorkingDays(calendar, predecessor.earlyFinish, 1 + lag);
        case 'SS':
            return addWorkingDays(calendar, predecessor.earlyStart, lag);
        case 'FF': {
            const finish = addWorkingDays(calendar, predecessor.earlyFinish, lag);
            return startOfTask(calendar, finish, successorDuration);
        }
        case 'SF': {
            const finish = addWorkingDays(calendar, predecessor.earlyStart, lag);
            return startOfTask(calendar, finish, successorDuration);
        }
    }
}
export function forwardPass(input) {
    const graphResult = buildGraph(input.tasks, input.dependencies);
    if (!graphResult.ok)
        return err(graphResult.error);
    const graph = graphResult.value;
    const projectStart = nextWorkingDay(input.calendar, requireIsoDate(input.projectStart));
    const schedules = new Map();
    // The topological order guarantees every predecessor is already scheduled
    // when its successor is reached, so one pass is enough.
    for (const taskId of graph.order) {
        const task = graph.tasks.get(taskId);
        if (!task)
            continue;
        let earlyStart = projectStart;
        // A pinned date raises the floor before the relationships are considered.
        // Both kinds only push later, so taking the maximum keeps them satisfied.
        if (task.constraint !== undefined) {
            const pinned = requireIsoDate(task.constraint.date);
            earlyStart = maxDate(earlyStart, task.constraint.type === 'startNoEarlierThan'
                ? pinned
                : startOfTask(input.calendar, pinned, task.durationDays));
        }
        for (const dependency of graph.predecessors.get(taskId) ?? []) {
            const predecessor = schedules.get(dependency.predecessorId);
            if (!predecessor)
                continue;
            const candidate = earliestStartFrom(input.calendar, dependency, predecessor, task.durationDays);
            // A negative lag may pull a task before the project start; the project
            // start wins, so nothing is scheduled before the project begins.
            earlyStart = maxDate(earlyStart, candidate);
        }
        earlyStart = nextWorkingDay(input.calendar, earlyStart);
        schedules.set(taskId, {
            id: taskId,
            earlyStart,
            earlyFinish: endOfTask(input.calendar, earlyStart, task.durationDays),
            durationDays: task.durationDays,
        });
    }
    let projectFinish = projectStart;
    for (const schedule of schedules.values()) {
        projectFinish = maxDate(projectFinish, schedule.earlyFinish);
    }
    return ok({ schedules, projectFinish, order: graph.order, graph });
}
/**
 * Checks that a computed schedule really satisfies every relationship.
 *
 * Not needed in production - the forward pass constructs a valid schedule by
 * definition - but it turns "the numbers look plausible" into "every constraint
 * was verified", which is what the tests assert.
 */
export function violatedDependencies(calendar, schedules, dependencies) {
    const violated = [];
    for (const dependency of dependencies) {
        const predecessor = schedules.get(dependency.predecessorId);
        const successor = schedules.get(dependency.successorId);
        if (!predecessor || !successor)
            continue;
        const required = earliestStartFrom(calendar, dependency, predecessor, successor.durationDays);
        if (successor.earlyStart < required)
            violated.push(dependency);
    }
    return violated;
}
//# sourceMappingURL=forward-pass.js.map